import { useEffect, useRef, useSyncExternalStore } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText as GSAPSplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger, GSAPSplitText, useGSAP);

const DEFAULT_FROM = { opacity: 0, y: 40 };
const DEFAULT_TO = { opacity: 1, y: 0 };

const fontsReady = () => !document.fonts || document.fonts.status === "loaded";
const serverFontsReady = () => false;
function subscribeFonts(listener) {
  const fonts = document.fonts;
  if (!fonts) return () => {};
  let active = true;
  const changed = () => { if (active) listener(); };
  fonts.addEventListener("loadingdone", changed);
  fonts.addEventListener("loadingerror", changed);
  fonts.ready.then(changed);
  return () => {
    active = false;
    fonts.removeEventListener("loadingdone", changed);
    fonts.removeEventListener("loadingerror", changed);
  };
}

function getScrollStart(threshold, rootMargin) {
  const startPercent = (1 - threshold) * 100;
  const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
  const marginValue = marginMatch ? Number.parseFloat(marginMatch[1]) : 0;
  const marginUnit = marginMatch?.[2] || "px";
  const offset = marginValue === 0
    ? ""
    : marginValue < 0
      ? `-=${Math.abs(marginValue)}${marginUnit}`
      : `+=${marginValue}${marginUnit}`;

  return `top ${startPercent}%${offset}`;
}

export default function SplitText({
  text = "",
  className = "",
  delay = 50,
  duration = 1.25,
  ease = "power3.out",
  splitType = "chars",
  from = DEFAULT_FROM,
  to = DEFAULT_TO,
  threshold = 0.1,
  rootMargin = "-100px",
  textAlign = "center",
  display = "inline-block",
  smartWrap = true,
  tag = "p",
  manual = false,
  play = true,
  onLetterAnimationComplete,
}) {
  const ref = useRef(null);
  const animationCompletedRef = useRef(false);
  const onCompleteRef = useRef(onLetterAnimationComplete);
  const fontsLoaded = useSyncExternalStore(subscribeFonts, fontsReady, serverFontsReady);

  useEffect(() => {
    onCompleteRef.current = onLetterAnimationComplete;
  }, [onLetterAnimationComplete]);

  useGSAP(
    () => {
      if (!ref.current || !text || !fontsLoaded || animationCompletedRef.current) return undefined;

      const element = ref.current;
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (prefersReducedMotion) {
        animationCompletedRef.current = true;
        onCompleteRef.current?.();
        return undefined;
      }

      element._rbsplitInstance?.revert();
      element._rbsplitInstance = null;

      let tween;
      const splitInstance = new GSAPSplitText(element, {
        type: splitType,
        smartWrap,
        autoSplit: splitType.includes("lines"),
        linesClass: "split-line",
        wordsClass: "split-word",
        charsClass: "split-char",
        reduceWhiteSpace: false,
        onSplit: (self) => {
          const targets = splitType.includes("chars") && self.chars.length
            ? self.chars
            : splitType.includes("words") && self.words.length
              ? self.words
              : self.lines;

          if (manual && !play) {
            gsap.set(targets, {
              ...from,
              force3D: true,
              willChange: "transform, opacity",
            });
            return undefined;
          }

          const tweenVars = {
            ...to,
            duration,
            ease,
            stagger: delay / 1000,
            force3D: true,
            willChange: "transform, opacity",
            onComplete: () => {
              animationCompletedRef.current = true;
              gsap.set(targets, { clearProps: "transform,opacity,willChange" });
              onCompleteRef.current?.();
            },
          };

          if (!manual) {
            tweenVars.scrollTrigger = {
              trigger: element,
              start: getScrollStart(threshold, rootMargin),
              once: true,
              fastScrollEnd: true,
              anticipatePin: 0.4,
            };
          }

          tween = gsap.fromTo(
            targets,
            { ...from },
            tweenVars,
          );

          return tween;
        },
      });

      element._rbsplitInstance = splitInstance;

      return () => {
        tween?.scrollTrigger?.kill();
        tween?.kill();
        splitInstance.revert();
        element._rbsplitInstance = null;
      };
    },
    {
      dependencies: [
        text,
        delay,
        duration,
        ease,
        splitType,
        JSON.stringify(from),
        JSON.stringify(to),
        threshold,
        rootMargin,
        smartWrap,
        fontsLoaded,
        manual,
        play,
      ],
      scope: ref,
      revertOnUpdate: true,
    },
  );

  const Tag = tag || "p";
  const hideUntilManualReveal = manual && (!play || !fontsLoaded);

  return (
    <Tag
      ref={ref}
      className={`split-parent ${className}`.trim()}
      style={{
        textAlign,
        overflow: "hidden",
        display,
        whiteSpace: text.includes("\n") ? "pre-line" : "var(--split-text-white-space, normal)",
        overflowWrap: "var(--split-text-overflow-wrap, break-word)",
        willChange: "transform, opacity",
        visibility: hideUntilManualReveal ? "hidden" : undefined,
      }}
    >
      {text}
    </Tag>
  );
}
